import React from 'react';
import { Alert, Button, Modal } from '@odin-labs/components';
import {
  useGetSiteReaderAttachmentsQuery,
  useRemoveSiteReaderAttachmentMutation,
} from 'apollo/generated/client-operations';

type Props = {
  siteId: string;
  assignmentId: string;
  readerDeviceId: string;
  name: string;
  disabled?: boolean;
};

/** Explicit provider read: never turn an unread or failed response into an empty list. */
export function ReaderAttachments({
  siteId,
  assignmentId,
  readerDeviceId,
  name,
  disabled,
}: Props): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const input = { siteId, assignmentId, readerDeviceId };
  const query = useGetSiteReaderAttachmentsQuery({
    variables: { input },
    skip: !expanded,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [remove, mutation] = useRemoveSiteReaderAttachmentMutation();
  const snapshot = query.data?.siteReaderAttachments;
  const attachment = snapshot?.attachments.find(
    (row) => row.entryId === selected,
  );
  const busy = query.loading || mutation.loading;
  const confirm = async (): Promise<void> => {
    if (!attachment || !snapshot || busy || disabled) return;
    setMessage(null);
    try {
      await remove({
        variables: {
          input: {
            ...input,
            entryId: attachment.entryId,
            expectedFingerprint: snapshot.fingerprint,
          },
        },
      });
      setSelected(null);
      await query.refetch();
      setMessage('Attachment removed and remaining attachments verified.');
    } catch (error) {
      setSelected(null);
      setMessage(
        error instanceof Error
          ? error.message
          : 'The removal could not be confirmed. Refresh before retrying.',
      );
      await query.refetch().catch((): void => undefined);
    }
  };
  return (
    <div className="odin-space-y-2 odin-border-t odin-pt-3">
      <div className="odin-font-medium">{name}</div>
      <Button
        text={expanded ? 'Refresh attachments' : 'Show Avigilon attachments'}
        theme="white"
        size="sm"
        disabled={busy || disabled}
        onClick={() => {
          setSelected(null);
          if (expanded) void query.refetch().catch((): void => undefined);
          else setExpanded(true);
        }}
      />
      {query.loading && <p>Reading Avigilon attachments…</p>}
      {query.error && (
        <Alert
          type="danger"
          text={`Could not read attachments: ${query.error.message}`}
        />
      )}
      {message && <Alert type="warn" text={message} />}
      {expanded && !query.loading && !query.error && snapshot && (
        <>
          {snapshot.attachments.length === 0 && (
            <p>No entry attachments in Avigilon.</p>
          )}
          {snapshot.attachments.map((row) => (
            <div
              key={row.entryId}
              className="odin-space-y-1 odin-rounded odin-border odin-p-3"
            >
              <p>
                {row.siteName} → {row.zoneName} → {row.entryName}
              </p>
              <p className="odin-text-xs odin-text-gray-500">
                {row.currentSite ? 'This site' : 'Another Avigilon site'} ·
                Entry {row.entryId}
              </p>
              {row.removalBlockedReason ? (
                <p className="odin-text-sm">{row.removalBlockedReason}</p>
              ) : (
                <Button
                  text="Remove attachment"
                  theme="danger"
                  size="sm"
                  disabled={busy || disabled}
                  onClick={() => setSelected(row.entryId)}
                />
              )}
            </div>
          ))}
        </>
      )}
      <Modal
        open={Boolean(attachment)}
        setOpen={(next) => {
          if (!next && !mutation.loading) setSelected(null);
        }}
        title="Remove reader attachment?"
        size="sm"
        actionText="Remove attachment"
        actionButtonTheme="danger"
        actionButtonEnabled={
          !busy && !disabled && Boolean(attachment?.removable)
        }
        onAction={confirm}
        cancelText="Keep attachment"
        cancelButtonEnabled={!mutation.loading}
        onCancel={() => setSelected(null)}
      >
        {attachment && (
          <div className="odin-space-y-3">
            <p>
              Remove {name} from {attachment.siteName} → {attachment.zoneName} →{' '}
              {attachment.entryName}?
            </p>
            <p>
              This stops this reader from granting access through that entry.
              Its other attachments stay in place.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
